import React, { useState } from 'react';
import { useImages } from '../context/ImageContext';
import Footer from '../components/Footer';
import { API_URL } from '../config/api';
import { pushFormSubmit } from '../utils/gtm';
import './ReferFriendPage.css';

const ReferFriendPage: React.FC = () => {
  const { getImage } = useImages();
  
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [formData, setFormData] = useState({
    yourFirstName: '',
    yourLastName: '',
    yourPhone: '',
    friendFirstName: '',
    friendLastName: '',
    friendPhone: '',
    bestTime: 'Any Time',
  });
  
  const [errors, setErrors] = useState<{[key: string]: string}>({});
  const birdsImage = getImage('refer-a-friend-birds1.webp');

  const validateStep1 = () => {
    const newErrors: {[key: string]: string} = {};
    
    if (!formData.yourFirstName.trim()) newErrors.yourFirstName = 'First name is required';
    if (!formData.yourLastName.trim()) newErrors.yourLastName = 'Last name is required';
    if (!formData.yourPhone.trim()) newErrors.yourPhone = 'Phone number is required';
    if (!formData.friendFirstName.trim()) newErrors.friendFirstName = 'Friend\'s first name is required';
    if (!formData.friendLastName.trim()) newErrors.friendLastName = 'Friend\'s last name is required';
    if (!formData.friendPhone.trim()) newErrors.friendPhone = 'Friend\'s phone number is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleStep1Next = () => {
    if (validateStep1()) {
      setStep(2);
      setSubmitError('');
    }
  };

  const handleSubmitReferral = async () => {
    if (submitting) return;

    setSubmitting(true);
    setSubmitError('');

    try {
      const response = await fetch(`${API_URL}/api/referrals`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          yourFirstName: formData.yourFirstName.trim(),
          yourLastName: formData.yourLastName.trim(),
          yourPhone: formData.yourPhone.trim(),
          friendFirstName: formData.friendFirstName.trim(),
          friendLastName: formData.friendLastName.trim(),
          friendPhone: formData.friendPhone.trim(),
          bestTime: formData.bestTime,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to submit referral');
      }

      pushFormSubmit({ formId: 'refer_friend', formName: 'Refer a Friend' });
      setStep(3);
    } catch (error) {
      console.error('Error submitting referral:', error);
      setSubmitError(
        'There was an error submitting your referral. Please try again or call us at 1-877-579-9862.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const scrollToForm = () => {
    const formSection = document.getElementById('refer-form-section');
    if (formSection) {
      formSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="refer-friend-page">
      {/* Hero Section - Text Left, Image Right */}
      <section className="refer-hero">
        <div className="refer-hero-container">
          <div className="refer-hero-content">
            <div className="refer-hero-text">
              <h1>Earn one <span className="free-text">FREE</span> month<br />for you and your friend</h1>
              <button className="refer-cta-btn" onClick={scrollToForm}>KEEP MY FRIEND SAFE!</button>
            </div>
            <div className="refer-hero-image-wrapper">
              {birdsImage && <img src={birdsImage} alt="Refer a Friend" className="refer-hero-image" />}
            </div>
          </div>
        </div>
      </section>

      {/* Steps Section - 3 Columns */}
      <section id="refer-form-section" className="refer-steps-section">
        <div className="container">
          <h2>Just 3 steps to get one free month of protection</h2>
          
          <div className={`refer-steps-grid ${step === 3 ? 'step-3-layout' : ''}`}>
            {/* Step 1 Column */}
            <div className={`refer-step-column ${step === 1 ? 'step-active' : ''} ${step === 3 ? 'step-completed-compact' : ''}`}>
              <div className={`step-number ${step >= 1 ? 'filled' : 'outlined'}`}>1</div>
              <h3>You Share</h3>
              
              {step === 1 ? (
                <div className="step-1-form">
                  <div className="form-row">
                    <div className="form-section">
                      <h4>Your Information</h4>
                      <div className="form-group">
                        <input
                          type="text"
                          placeholder="First Name"
                          value={formData.yourFirstName}
                          onChange={(e) => handleChange('yourFirstName', e.target.value)}
                          className={errors.yourFirstName ? 'error' : ''}
                        />
                        {errors.yourFirstName && <span className="error-text">{errors.yourFirstName}</span>}
                      </div>
                      <div className="form-group">
                        <input
                          type="text"
                          placeholder="Last Name"
                          value={formData.yourLastName}
                          onChange={(e) => handleChange('yourLastName', e.target.value)}
                          className={errors.yourLastName ? 'error' : ''}
                        />
                        {errors.yourLastName && <span className="error-text">{errors.yourLastName}</span>}
                      </div>
                      <div className="form-group">
                        <input
                          type="tel"
                          placeholder="Phone Number"
                          value={formData.yourPhone}
                          onChange={(e) => handleChange('yourPhone', e.target.value)}
                          className={errors.yourPhone ? 'error' : ''}
                        />
                        {errors.yourPhone && <span className="error-text">{errors.yourPhone}</span>}
                      </div>
                    </div>

                    <div className="form-section">
                      <h4>Friend's Information</h4>
                      <div className="form-group">
                        <input
                          type="text"
                          placeholder="First Name"
                          value={formData.friendFirstName}
                          onChange={(e) => handleChange('friendFirstName', e.target.value)}
                          className={errors.friendFirstName ? 'error' : ''}
                        />
                        {errors.friendFirstName && <span className="error-text">{errors.friendFirstName}</span>}
                      </div>
                      <div className="form-group">
                        <input
                          type="text"
                          placeholder="Last Name"
                          value={formData.friendLastName}
                          onChange={(e) => handleChange('friendLastName', e.target.value)}
                          className={errors.friendLastName ? 'error' : ''}
                        />
                        {errors.friendLastName && <span className="error-text">{errors.friendLastName}</span>}
                      </div>
                      <div className="form-group">
                        <input
                          type="tel"
                          placeholder="Phone Number"
                          value={formData.friendPhone}
                          onChange={(e) => handleChange('friendPhone', e.target.value)}
                          className={errors.friendPhone ? 'error' : ''}
                        />
                        {errors.friendPhone && <span className="error-text">{errors.friendPhone}</span>}
                      </div>
                    </div>
                  </div>

                  <button className="btn-primary refer-next-btn" onClick={handleStep1Next}>
                    NEXT
                  </button>
                </div>
              ) : step < 3 ? (
                <div className="step-summary">
                  <p>{formData.yourFirstName} {formData.yourLastName}</p>
                  <p>{formData.yourPhone}</p>
                  <p>{formData.friendFirstName} {formData.friendLastName}</p>
                  <p>{formData.friendPhone}</p>
                </div>
              ) : null}
            </div>

            {/* Step 2 Column */}
            <div className={`refer-step-column ${step === 2 ? 'step-active' : ''} ${step === 3 ? 'step-completed-compact' : ''}`}>
              <div className={`step-number ${step >= 2 ? 'filled' : 'outlined'}`}>2</div>
              <h3>We Call</h3>
              
              {step < 2 ? (
                <p className="step-description">We will call your friend and offer them the peace of mind that you currently enjoy.</p>
              ) : step === 2 ? (
                <div className="step-2-content">
                  <p className="step-question">Best time to reach your friend?</p>
                  <div className="time-options-grid">
                    {['Any Time', 'Morning', 'Afternoon', 'Evening'].map(time => (
                      <label key={time} className="time-option-card">
                        <input
                          type="radio"
                          name="bestTime"
                          value={time}
                          checked={formData.bestTime === time}
                          onChange={(e) => handleChange('bestTime', e.target.value)}
                          disabled={submitting}
                        />
                        <span>{time}</span>
                      </label>
                    ))}
                  </div>
                  {submitError && <p className="error-text refer-submit-error">{submitError}</p>}
                  <button
                    className="btn-primary refer-next-btn"
                    onClick={handleSubmitReferral}
                    disabled={submitting}
                  >
                    {submitting ? 'SUBMITTING…' : 'SUBMIT REFERRAL'}
                  </button>
                </div>
              ) : step < 3 ? (
                <p className="step-description">Selected: {formData.bestTime}</p>
              ) : null}
            </div>

            {/* Step 3 Column */}
            <div className={`refer-step-column ${step === 3 ? 'step-active step-3-banner' : ''}`}>
              <div className={`step-number ${step >= 3 ? 'filled' : 'outlined'}`}>3</div>
              <h3>Earn FREE Month</h3>
              
              {step < 3 ? (
                <p className="step-description">As soon as they sign up with Alert1 you will have both earned a free month!</p>
              ) : (
                <div className="step-3-banner-content">
                  <p className="success-message-text">Thank you! We'll reach out to your friend soon.</p>
                  <p className="step-description">As soon as they sign up with Alert1 you will have both earned a free month!</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="refer-testimonial">
        <div className="container">
          <div className="testimonial-content">
            <p className="testimonial-label">Member: Susan</p>
            <p className="testimonial-quote">
              "2 weeks after I referred my friend, she fell and used her Alert1 system to get help. Thank you Alert1 for keeping us both safe!"
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ReferFriendPage;
